import { T } from '@/i18n';
import { today } from '@/rules';
import { DateJump } from '@/ui/DateJump';
/** Formatting shared by the screens: dates as the manager writes them, names, numbers, the week navigator. */
import { addDays, isoWeek, mondayOf } from '@timefly/core';
import { Icon } from './icons';

/**
 * Day and month names, translated as they are read: over a hundred call sites index these arrays, and
 * the Shell remounts the pages on a language switch, so a read-time T() follows the language.
 * ponytail: a Proxy over a plain array, so no call site changes; swap for Intl names if the lists grow.
 */
const named = (list: string[]): string[] =>
  new Proxy(list, {
    get: (target, key, receiver) =>
      typeof key === 'string' && /^\d+$/.test(key) && key in target
        ? T(target[Number(key)] as string)
        : Reflect.get(target, key, receiver),
  });
export const DN = named(['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn']);
export const DNL = named(['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag']);
export const MONTHS = named([
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
]);
/**
 * A month as every period control writes it: «oktober 2026», lower case as Norwegian writes months and
 * as Vaktplan and the calendar already did (#154 VF-07; Timer and Arbeidstid capitalised it).
 */
export const monthLabel = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTHS[(m ?? 1) - 1] ?? ym} ${y}`;
};
/** A day as a sentence writes it: «2. november» (Kom i gang and Kontroll's waiting state, #294). */
export const dayMonth = (d: string) =>
  `${Number(d.slice(8, 10))}. ${MONTHS[Number(d.slice(5, 7)) - 1] ?? ''}`;
/** The one placeholder for a figure that is not known, or has no value, on every screen (#272 A-R07). */
export const NO_VALUE = '–';
export const num = (n: number, digits = 2) => n.toLocaleString('nb-NO', { maximumFractionDigits: digits });
export const fmtDs = (d: string) => `${Number(d.slice(8, 10))}.${Number(d.slice(5, 7))}`;
/**
 * A stored TIME value as the clock shows it. The column is `TIME`
 * (`backend/models/shiftTaskAssignment.js`), so the API sends `14:00:00`;
 * seconds carry nothing for a task on a shift. Anything not in that shape is
 * returned untouched.
 */
export const hhmm = (t: string) => (/^\d{2}:\d{2}/.test(t) ? t.slice(0, 5) : t);
export const fmtD = (d: string) => `${fmtDs(d)}.${d.slice(0, 4)}`;
/**
 * The palette class a person's avatar gets.
 *
 * Every `.av` was the same slate, so a list of twenty colleagues was twenty
 * identical circles. This spreads them over the eight palette colours, keyed on
 * something stable so the same person is the same colour on every screen. Each
 * class pairs a fill with its own ink, which the tokens already document at
 * between 8.5:1 and 10.5:1.
 *
 * The phone app does the same, from the same eight, in lib/theme avatarTone.
 */
export const avTone = (seed: string | number | null | undefined) => {
  const key = String(seed ?? '');
  let n = 0;
  for (let i = 0; i < key.length; i += 1) n = (n + key.charCodeAt(i)) % 997;
  return `t${n % 8}`;
};

export const initials = (n: string) =>
  n
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
/**
 * One name part in the case a person writes it: lowercase the whole thing, then capitalise
 * each whitespace-separated word. The split matters because the `name` column holds the
 * given names together, so "jon erik" has to become "Jon Erik" and not "Jon erik".
 *
 * It is done here rather than in the API because the API has no single place to do it in.
 * `User.prototype.serialize` and `User.colleagueView` (backend/models/user.js:109, :132) cover
 * GET /users and the person's own record, but every other payload builds its own projection:
 * the shift rows this schedule renders come from `admin/shiftController.js:369`
 * (`{ id, name, lastname }`, by hand), and absences, chat, documents and shift tasks each
 * select the two columns straight from the row. `fullName` is the one place every screen
 * already goes through. Written 2026-09-20 on the owner's instruction about mixed case.
 */
// Capitalised after a start, a space and a hyphen, as the server's notifications write names
// (backend/services/norsk.js navn): "anne-marie NORDMANN" is "Anne-Marie Nordmann" on both.
export const properName = (s: string | null | undefined): string =>
  (s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('nb-NO')
    .replace(/(^|[\s-])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toLocaleUpperCase('nb-NO'));
export const fullName = (u: { name: string; lastname?: string | null } | null | undefined) =>
  u ? [properName(u.name), properName(u.lastname)].filter(Boolean).join(' ') : '';

export function WeekNav({ monday, onChange }: { monday: string; onChange: (m: string) => void }) {
  return (
    <div className="wk">
      <button type="button" onClick={() => onChange(addDays(monday, -7))} aria-label={T('Forrige uke')}>
        <Icon name="chevronLeft" />
      </button>
      <DateJump period="week" value={monday} onChange={(d) => onChange(mondayOf(d))}>
        <span className="lbl">
          {T('Uke')} {isoWeek(monday)}, {fmtDs(monday)}–{fmtDs(addDays(monday, 6))}
        </span>
      </DateJump>
      <button type="button" onClick={() => onChange(addDays(monday, 7))} aria-label={T('Neste uke')}>
        <Icon name="chevronRight" />
      </button>
      {monday !== mondayOf(today()) && (
        <button type="button" style={{ fontSize: 12 }} onClick={() => onChange(mondayOf(today()))}>
          {T('I dag')}
        </button>
      )}
    </div>
  );
}

/**
 * An absence type's name as a person reads it. Deleting a type renames it "Lege (Deleted)" in the
 * database (absenceTypeControlller.js, delete), and that suffix reached every screen; it reads
 * "Lege (slettet)" instead, so an old request stays readable. No type gives the fallback.
 */
export const typeName = (t: { name?: string | null } | null | undefined, fallback = T('Fravær')): string =>
  t?.name ? t.name.replace(/\s*\(Deleted\)\s*$/i, ` (${T('slettet')})`) : fallback;

/** A Norwegian number as people write it: "939 36 306", "+47 939 36 306". Anything else as typed. */
export const fmtPhone = (raw: string | null | undefined): string => {
  const v = (raw ?? '').trim();
  const d = v.replace(/[\s-]/g, '');
  const m = /^(\+47|0047)?(\d{8})$/.exec(d);
  if (!m) return v;
  const n = m[2] as string;
  return `${m[1] ? '+47 ' : ''}${n.slice(0, 3)} ${n.slice(3, 5)} ${n.slice(5)}`;
};
